import { decodeNumericEntities } from '../lib/v1text';
import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiGet, apiPatch, apiPost } from '../lib/api';
import type { TicketDetailResponse } from '../types';
import { useAuth, useFeature } from '../context/AuthContext';
import { useRefs } from '../context/RefsContext';
import { UserAvatar, UserName } from '../components/UserAvatar';
import { supportName } from '../lib/people';
import { MessageComposer } from '../components/MessageComposer';
import { StateActions } from '../components/StateActions';
import { AttachmentList } from '../components/AttachmentList';
import { ClientLogo } from '../components/ClientLogo';
import { isDtStaff } from '../lib/staff';
import { messageSide } from '../lib/messageSide';
import { parseMessageOrder } from '../lib/preferences';
import { TicketEditor } from '../components/TicketEditor';
import { HorsTmaToggle } from '../components/HorsTmaToggle';
import { DeclarantEditor } from '../components/DeclarantEditor';
import { ReadBadge, ReadHistoryLink } from '../components/ReadHistory';
import { PrioEtatCase, prioEtatLabel } from '../components/PrioEtatCase';

function AssigneesEditor({
  displayId,
  supportIds,
  supportUsers,
  onChanged,
}: {
  displayId: number;
  supportIds: number[];
  supportUsers?: { id_user: number; nom_user: string }[];
  onChanged: () => void;
}) {
  const { user } = useAuth();
  const { traitants } = useRefs();
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<number[]>(supportIds);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const assignOpen = useFeature('ticket_assign');
  const canEdit = isDtStaff(user) && assignOpen;

  useEffect(() => {
    setSelected(supportIds);
  }, [supportIds.join(',')]);

  function toggle(id: number) {
    setSelected((curr) => (curr.includes(id) ? curr.filter((n) => n !== id) : [...curr, id]));
  }

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      await apiPatch(`/api/tickets/${displayId}/assignees`, { id_support: selected });
      setEditing(false);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <div>
        {supportIds.length === 0 && <div style={{ color: 'var(--text-mute)' }}>Aucun traitant assigné</div>}
        {supportIds.map((id) => (
          <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <UserAvatar id={id} size={20} name={supportName(supportUsers, id)} />
            <UserName id={id} name={supportName(supportUsers, id)} />
          </div>
        ))}
        {canEdit && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            style={{
              marginTop: 6,
              fontSize: 'var(--fs-xs)',
              padding: '4px 8px',
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: 4,
              cursor: 'pointer',
              color: 'var(--accent)',
            }}
          >
            ✎ Modifier
          </button>
        )}
      </div>
    );
  }

  const sorted = [...traitants].sort((a, b) => a.nom_user.localeCompare(b.nom_user));
  return (
    <div>
      <div style={{ display: 'grid', gap: 4, maxHeight: 220, overflow: 'auto', marginBottom: 8 }}>
        {sorted.map((t) => (
          <label key={t.id_user} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--fs-xs)' }}>
            <input type="checkbox" checked={selected.includes(t.id_user)} onChange={() => toggle(t.id_user)} />
            {t.nom_user}
          </label>
        ))}
      </div>
      {err && <div style={{ color: 'var(--danger)', fontSize: 'var(--fs-xs)', marginBottom: 6 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          style={{
            fontSize: 'var(--fs-xs)',
            padding: '4px 10px',
            background: 'var(--accent)',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
          }}
        >
          {saving ? '…' : 'Enregistrer'}
        </button>
        <button
          type="button"
          onClick={() => {
            setEditing(false);
            setSelected(supportIds);
            setErr(null);
          }}
          style={{
            fontSize: 'var(--fs-xs)',
            padding: '4px 10px',
            background: 'transparent',
            border: '1px solid var(--border)',
            borderRadius: 4,
            cursor: 'pointer',
          }}
        >
          Annuler
        </button>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

interface V1ActivityItem {
  login: string;
  nom_user: string | null;
  action: string;
  minutes_ago: number;
}

/**
 * Alerte d'édition concurrente (décision E9) : le formulaire du V1 réécrit
 * tous les champs du point à chaque enregistrement, et écrase donc ce que la
 * V2 a changé entre-temps (état, traitants…). On signale au staff que le point
 * a été ouvert ou enregistré dans le V1 récemment (table `log` du V1).
 */
function V1ActivityBanner({ displayId, refreshKey }: { displayId: number; refreshKey: unknown }) {
  const { user } = useAuth();
  const [items, setItems] = useState<V1ActivityItem[]>([]);
  const [windowMin, setWindowMin] = useState(60);
  const isStaff = isDtStaff(user);
  useEffect(() => {
    if (!isStaff) return;
    const ctl = new AbortController();
    apiGet<{ window_minutes: number; items: V1ActivityItem[] }>(`/api/tickets/${displayId}/v1-activity`, ctl.signal)
      .then((r) => {
        setItems(r.items);
        setWindowMin(r.window_minutes);
      })
      .catch(() => {});
    return () => ctl.abort();
  }, [displayId, isStaff, refreshKey]);
  if (!isStaff || items.length === 0) return null;
  const verb = (a: string) => (a === 'save_bug' ? 'enregistré' : a === 'val_bug' ? 'validé' : 'ouvert');
  return (
    <div
      role="alert"
      style={{
        background: 'var(--warning-soft)',
        border: '1px solid var(--warning-border)',
        borderRadius: 8,
        padding: '10px 14px',
        marginBottom: 12,
        fontSize: 'var(--fs-sm)',
      }}
    >
      <strong>⚠ Point actif dans Jarvis V1</strong> (dernières {windowMin} min) :{' '}
      {items
        .map((i) => `${verb(i.action)} par ${i.nom_user ?? i.login} il y a ${i.minutes_ago} min`)
        .join(' ; ')}
      . Un enregistrement du formulaire V1 ouvert avant vos modifications les écrasera (état, traitants, titre, priorité, notifiés, évaluation…).
    </div>
  );
}

function formatTemps(decimalHours: number | null): string {
  if (!decimalHours || decimalHours === 0) return '0 H 00';
  const h = Math.floor(decimalHours);
  const m = Math.round((decimalHours - h) * 60);
  return `${h} H ${m.toString().padStart(2, '0')}`;
}

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<TicketDetailResponse | null>(null);
  const { user } = useAuth();
  // Interrupteurs (cohabitation V1) : une action fermée n'est pas proposée.
  const stateOpen = useFeature('ticket_state');
  const stateClientOpen = useFeature('ticket_state_client');
  // Agents Digital Team : ticket_state ; clients (boutons Valider, Refuser…) :
  // en plus ticket_state_client, fermé par défaut (l'API refuse sinon).
  const canChangeState = stateOpen && (isDtStaff(user) || stateClientOpen);
  const canPostMessage = useFeature('messages');
  const canLogTime = useFeature('time_entry');
  const canUpload = useFeature('attach_upload');
  const [err, setErr] = useState<string | null>(null);
  // Pièce ajoutée depuis la zone de réponse : le bloc Pièces jointes se relit.
  const [attachRefresh, setAttachRefresh] = useState(0);
  const onAttached = useCallback(() => setAttachRefresh((n) => n + 1), []);

  const load = useCallback(() => {
    if (!id) return () => {};
    const ctl = new AbortController();
    setErr(null);
    apiGet<TicketDetailResponse>(`/api/tickets/${id}`, ctl.signal)
      .then((d) => {
        if (!ctl.signal.aborted) setData(d);
      })
      .catch((e) => {
        if (ctl.signal.aborted) return;
        setErr(e instanceof Error ? e.message : 'Erreur');
      });
    return () => ctl.abort();
  }, [id]);

  useEffect(() => {
    setData(null);
    return load();
  }, [load]);

  // Marque les messages comme lus à l'ouverture, comme le V1 : seulement pour
  // un utilisateur hors Digital Team (id_client != 3), et seulement si les
  // messages sont ouverts — le marquage écrit tache_message.vu_client, une
  // table du V1, et l'API le bloque en lecture seule.
  const readerIdClient = user?.id_client;
  useEffect(() => {
    if (!id || !canPostMessage || readerIdClient === undefined || readerIdClient === 3) return;
    void apiPost(`/api/tickets/${id}/read`, {}).catch(() => {});
  }, [id, canPostMessage, readerIdClient]);

  if (err) {
    return (
      <div style={{ padding: 20, color: 'var(--danger)' }}>
        {err} — <Link to="/tickets?preset=a_traiter">retour à la liste</Link>
      </div>
    );
  }
  if (!data) return <div style={{ color: 'var(--text-mute)' }}>Chargement…</div>;
  const { ticket, messages } = data;
  // Décision E6 : un point Finalisé est figé pour les clients (comme le V1),
  // le staff peut encore écrire et le rouvrir.
  const frozenForClient = !isDtStaff(user) && ticket.etat === 'Finalisé';
  // Ordre du fil : préférence du compte (Profil), plus récents en haut par
  // défaut. Tri sur la date puis le n° : ne dépend pas de l'ordre de l'API.
  const recentFirst = parseMessageOrder(user?.preferences?.message_order) === 'recent_first';
  const orderedMessages = [...messages].sort((a, b) => {
    const d = new Date(a.date_message).getTime() - new Date(b.date_message).getTime() || a.id_tache_message - b.id_tache_message;
    return recentFirst ? -d : d;
  });
  // Pièces jointes du point : colonne compacte à droite de la zone de réponse
  // (le trombone et le dépôt sont dans la carte). Sans zone de réponse (point
  // finalisé pour un client, messages fermés), même colonne à droite de
  // l'avis, avec son propre bouton « Ajouter » si le dépôt reste permis.
  const attachments = (withUpload: boolean) => (
    <AttachmentList displayId={ticket.display_id} canUpload={withUpload} refreshKey={attachRefresh} />
  );
  const withAttachments = (notice: React.ReactNode) => (
    <div className="composer-grid">
      <div className="composer-main">{notice}</div>
      <div className="composer-aside attach-card">{attachments(canUpload && !frozenForClient)}</div>
    </div>
  );
  const replyArea = frozenForClient ? (
    withAttachments(
      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', border: '1px dashed var(--border)', borderRadius: 8, padding: '8px 12px', margin: '10px 0' }}>
        Ce point est finalisé : il ne peut plus recevoir de message. Pour une nouvelle demande, créez un nouveau point.
      </div>,
    )
  ) : canPostMessage ? (
    <MessageComposer
      displayId={ticket.display_id}
      onPosted={load}
      canAttach={canUpload}
      onAttached={onAttached}
      aside={attachments(false)}
    />
  ) : (
    withAttachments(<ReadOnlyNotice what="Pour répondre" />)
  );

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Link to="/tickets?preset=a_traiter" style={{ fontSize: 'var(--fs-xs)' }}>
          ← retour
        </Link>
        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', marginTop: 6 }}>
          Point #{ticket.display_id} · {ticket.client_nom}
          {ticket.hors_tma && <span className="badge-hors-tma" style={{ marginLeft: 8 }}>Hors TMA</span>}
          <span style={{ marginLeft: 10 }}>
            <ReadHistoryLink displayId={ticket.display_id} />
          </span>
        </div>
        <h1 style={{ fontSize: 'var(--fs-xl)', fontWeight: 600, margin: '4px 0 0', overflowWrap: 'anywhere' }}>{ticket.nom_tache}</h1>
      </div>

      <V1ActivityBanner displayId={ticket.display_id} refreshKey={data} />

      <div className="detail-grid">
        <div>
          {ticket.description && (
            <div
              style={{
                background: 'var(--accent-soft)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: '14px 16px',
                marginBottom: 14,
              }}
            >
              <div style={{ fontWeight: 600, color: 'var(--accent-strong)', fontSize: 'var(--fs-sm)', marginBottom: 6 }}>
                📌 Demande initiale
              </div>
              <div className="user-text" style={{ fontSize: 'var(--fs-sm)' }}>{decodeNumericEntities(ticket.description)}</div>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', margin: '14px 0 8px' }}>
            <h3 style={{ fontSize: 'var(--fs-base)', fontWeight: 600, margin: 0 }}>Échanges ({messages.length})</h3>
            <div style={{ flex: 1 }} />
            {canChangeState && <StateActions displayId={ticket.display_id} currentEtat={ticket.etat} onChanged={load} />}
          </div>
          {/* Plus récents en haut : la zone de réponse vient avant le fil. */}
          {recentFirst && <div style={{ marginBottom: 14 }}>{replyArea}</div>}
          {messages.length === 0 && (
            <div style={{ color: 'var(--text-mute)', fontSize: 'var(--fs-sm)' }}>Aucun échange pour l&apos;instant.</div>
          )}
          {orderedMessages.map((m) => {
            const side = messageSide(user?.id_client, m);
            const interne = m.portee === 'interne';
            return (
              <div key={m.id_tache_message} className={`msg msg-${side}`}>
                <div className="avatar" style={{ flexShrink: 0 }} aria-hidden="true">
                  {m.auteur_nom
                    .split(/\s+/)
                    .map((w) => w[0])
                    .slice(0, 2)
                    .join('')
                    .toUpperCase()}
                </div>
                <div
                  className="msg-bubble"
                  style={{
                    background: interne ? 'var(--warning-soft)' : side === 'right' ? 'var(--accent-soft)' : 'var(--bg-soft)',
                    border: `1px solid ${interne ? 'var(--warning-border)' : 'var(--border)'}`,
                  }}
                >
                  <div className="msg-head">
                    <strong style={{ color: 'var(--text)' }}>{m.auteur_nom}</strong>
                    <span>
                      {formatDate(m.date_message)} ·{' '}
                      <span style={{ color: m.portee === 'public' ? 'var(--accent)' : 'var(--warning)' }}>{m.portee}</span>
                      {/* Marquage du V1 (vu_client), renvoyé aux agents DT administrateurs seulement. */}
                      {m.vu_client && new Date(m.vu_client).getTime() > new Date(m.date_message).getTime() + 60_000 && ` · ✓ Lu`}
                    </span>
                  </div>
                  <div className="user-text" style={{ fontSize: 'var(--fs-sm)' }}>{decodeNumericEntities(m.message)}</div>
                  <ReadBadge message={m} />
                </div>
              </div>
            );
          })}
          {/* Plus anciens en haut : la zone de réponse suit le dernier message. */}
          {!recentFirst && replyArea}
        </div>

        {/* Sidebar */}
        <aside style={{ fontSize: 'var(--fs-sm)' }}>
          <Block title="État & classification">
            <KV k="Type" v={ticket.type_tache} />
            {/* Même case colorée que la liste des points (priorité au-dessus,
                état dessous), toujours colorée : pas de ligne ici. */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '3px 0' }}>
              <span style={{ color: 'var(--text-mute)' }} aria-hidden="true">Priorité / État</span>
              <PrioEtatCase
                priorite={ticket.priorite}
                etat={ticket.etat}
                className="prio-case-fiche"
                role="img"
                aria-label={prioEtatLabel(ticket.priorite, ticket.etat)}
                title={prioEtatLabel(ticket.priorite, ticket.etat)}
              />
            </div>
            <KV k="Créé le" v={formatDate(ticket.date_creation)} />
            {ticket.risque === 'on' && <KV k="⚠ Risque" v="Décharge demandée" warn />}
            <TicketEditor ticket={ticket} onChanged={load} />
            <HorsTmaToggle ticket={ticket} onChanged={load} />
            <DeclarantEditor ticket={ticket} onChanged={load} />
          </Block>
          <Block title="Auteur">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ClientLogo id={ticket.id_client} name={ticket.client_nom} size={32} />
              <div>
                <div>{ticket.posteur_nom}</div>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>{ticket.client_nom}</div>
              </div>
            </div>
            <TicketEditor ticket={ticket} onChanged={load} authorOnly />
          </Block>
          <Block title={`Traitants (${ticket.support_ids.length})`}>
            <AssigneesEditor
              displayId={ticket.display_id}
              supportIds={ticket.support_ids}
              supportUsers={ticket.support_users}
              onChanged={load}
            />
          </Block>
          <Block title="Temps & crédit">
            <KV k="Évaluation" v={formatTemps(Number(ticket.eval_temps) || 0)} />
            <KV k="Tps passé" v={formatTemps(Number(ticket.total_temps) || 0)} />
            {ticket.total_decredit && Number(ticket.total_decredit) > 0 && (
              <KV k="Déc. crédit" v={`${ticket.total_decredit} min`} />
            )}
            {ticket.mode_tma === 'credit' && (
              <KV k="Solde client" v={`${ticket.client_credit_courant ?? 0} min`} warn={(ticket.client_credit_courant ?? 0) < 30} />
            )}
            {canLogTime && (
              <TimeLogger displayId={ticket.display_id} modeTma={ticket.mode_tma ?? ''} evalTemps={Number(ticket.eval_temps) || 0} onLogged={load} />
            )}
          </Block>
        </aside>
      </div>
    </div>
  );
}

// Fonction fermée pendant la cohabitation avec Jarvis V1 (cf. lib/features.ts).
function ReadOnlyNotice({ what }: { what: string }) {
  return (
    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', border: '1px dashed var(--border)', borderRadius: 8, padding: '8px 12px', margin: '10px 0' }}>
      {what}, utilisez Jarvis V1 : cette fonction n&apos;est pas encore ouverte dans la nouvelle application.
    </div>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--bg-soft)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: 12,
        marginBottom: 10,
      }}
    >
      <h5 style={{ margin: '0 0 6px', fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-mute)' }}>
        {title}
      </h5>
      {children}
    </div>
  );
}

function KV({ k, v, warn }: { k: string; v: string; warn?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
      <span style={{ color: 'var(--text-mute)' }}>{k}</span>
      <span style={{ color: warn ? 'var(--warning)' : 'var(--text)', fontWeight: warn ? 600 : 500 }}>{v}</span>
    </div>
  );
}

// Saisie (traitants uniquement) — reproduit le formulaire Perl mod_bug :
//   - Évaluation (heures) : toujours éditable → tache.eval_temps.
//   - UN champ de temps selon le mode du client :
//       forfait → « Ajouter du temps » (heures) → tache_temps
//       sinon (crédit / vide) → « Déc. crédit temps » (minutes) → tache_decredit + décompte
function TimeLogger({
  displayId,
  modeTma,
  evalTemps,
  onLogged,
}: {
  displayId: number;
  modeTma: string;
  evalTemps: number;
  onLogged: () => void;
}) {
  const { user } = useAuth();
  const [evalT, setEvalT] = useState(evalTemps ? String(evalTemps) : '');
  const [temps, setTemps] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok?: string; err?: string } | null>(null);

  if (!isDtStaff(user)) return null;
  const isForfait = modeTma === 'forfait';

  const submit = async () => {
    setMsg(null);
    const body: Record<string, string> = {};
    if (evalT.trim() !== '') body.eval_temps = evalT.trim();
    if (temps.trim() !== '') {
      if (isForfait) body.temps_passe = temps.trim();
      else body.temps_credit = temps.trim();
    }
    if (!body.eval_temps && !body.temps_passe && !body.temps_credit) {
      return setMsg({ err: 'Renseignez une éval ou un temps' });
    }
    setBusy(true);
    try {
      await apiPost(`/api/tickets/${displayId}/temps`, body);
      setTemps('');
      setMsg({ ok: 'Enregistré' });
      onLogged();
    } catch (e) {
      setMsg({ err: e instanceof Error ? e.message : 'Erreur' });
    } finally {
      setBusy(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: 5,
    marginTop: 2,
    border: '1px solid var(--border-strong)',
    borderRadius: 5,
    fontSize: 'var(--fs-xs)',
  };

  return (
    <div style={{ marginTop: 8, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
      <label style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', marginBottom: 6 }}>
        Évaluation (h)
        <input type="number" step="0.25" min="0" value={evalT} onChange={(e) => setEvalT(e.target.value)} style={inputStyle} />
      </label>
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
        <label style={{ flex: 1, fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>
          {isForfait ? 'Ajouter du temps (h)' : 'Déc. crédit temps (min)'}
          <input
            type="number"
            step={isForfait ? '0.25' : '1'}
            min="0"
            value={temps}
            onChange={(e) => setTemps(e.target.value)}
            style={inputStyle}
          />
        </label>
        <button className="btn primary" onClick={submit} disabled={busy} style={{ fontSize: 'var(--fs-xs)' }}>
          {busy ? '…' : 'OK'}
        </button>
      </div>
      {msg?.err && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--danger)', marginTop: 4 }}>{msg.err}</div>}
      {msg?.ok && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--success)', marginTop: 4 }}>{msg.ok}</div>}
    </div>
  );
}
